'use client';

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import Link from 'next/link';

interface GalleryImage {
  id: number;
  image: string;
}

interface GalleryProps {
  gallery: GalleryImage[];
}

const Gallery: React.FC<GalleryProps> = ({ gallery }) => {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Memoize filtered gallery to prevent recreation on every render
  const filteredGallery = useMemo(
    () => gallery?.filter((image, id) => id > 1 && id < 16) || [],
    [gallery]
  );

  const columnCount = Math.max(1, Math.min(filteredGallery.length, 7));

  useEffect(() => {
    if (isLightboxOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isLightboxOpen]);

  const openLightbox = useCallback((index: number) => {
    setCurrentIndex(index);
    setIsLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setIsLightboxOpen(false);
  }, []);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % filteredGallery.length);
  }, [filteredGallery.length]);

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + filteredGallery.length) % filteredGallery.length);
  }, [filteredGallery.length]);

  // Memoize handleKeyDown to prevent memory leaks
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsLightboxOpen(false);
    } else if (e.key === 'ArrowRight') {
      setCurrentIndex((prev) => (prev + 1) % filteredGallery.length);
    } else if (e.key === 'ArrowLeft') {
      setCurrentIndex((prev) => (prev - 1 + filteredGallery.length) % filteredGallery.length);
    }
  }, [filteredGallery.length]);

  useEffect(() => {
    if (isLightboxOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isLightboxOpen, handleKeyDown]);

  return (
    <>
      <GalleryWrapper $columns={columnCount}>
        {filteredGallery.map((image, index) => (
          <Link
            key={image.id}
            href="#"
            className="lightbox w-inline-block w-lightbox"
            onClick={(e) => {
              e.preventDefault();
              openLightbox(index);
            }}
          >
            <img
              src={image.image}
              alt="gallery"
              sizes="(max-width: 479px) 50vw, 14vw"
              className="lb-image"
            />
          </Link>
        ))}
      </GalleryWrapper>

      {isLightboxOpen && (
        <LightboxOverlay onClick={closeLightbox}>
          <LightboxContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={closeLightbox}>×</CloseButton>

            <PrevButton onClick={goToPrev}>‹</PrevButton>
            <NextButton onClick={goToNext}>›</NextButton>

            <MainImage
              src={filteredGallery[currentIndex]?.image}
              alt="Gallery image"
            />

            <ThumbnailStrip>
              {filteredGallery.map((image, index) => (
                <Thumbnail
                  key={image.id}
                  src={image.image}
                  alt="Thumbnail"
                  className={index === currentIndex ? 'active' : ''}
                  onClick={() => setCurrentIndex(index)}
                />
              ))}
            </ThumbnailStrip>

            <Counter>
              {currentIndex + 1} / {filteredGallery.length}
            </Counter>
          </LightboxContent>
        </LightboxOverlay>
      )}
    </>
  );
};

export default Gallery;

const GalleryWrapper = styled.div<{ $columns: number }>`
  position: relative;
  display: grid;
  grid-template-columns: repeat(${(props) => props.$columns}, 1fr);
  gap: 2px;

  .lightbox {
    position: relative;
    display: block;
    overflow: hidden;
    width: 100%;
    aspect-ratio: 16 / 9;
    border: 1px solid #fff;
  }

  .lb-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    cursor: pointer;
    transition: transform 0.3s ease;
  }

  .lb-image:hover {
    transform: scale(1.05);
  }

  @media (max-width: 479px) {
    grid-template-columns: repeat(${(props) => Math.min(props.$columns, 2)}, 1fr);
  }
`;

const LightboxOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.95);
  z-index: 100000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LightboxContent = styled.div`
  position: relative;
  width: 90vw;
  height: 90vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const MainImage = styled.img`
  max-width: 60vw;
  max-height: 70vh;
  object-fit: contain;
  user-select: none;

  @media (max-width: 768px) {
    max-width: 85vw;
    max-height: 60vh;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: transparent;
  border: none;
  color: white;
  font-size: 48px;
  cursor: pointer;
  z-index: 10;
  line-height: 1;
  padding: 0;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
`;

const PrevButton = styled.button`
  position: absolute;
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid white;
  color: white;
  font-size: 48px;
  cursor: pointer;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  padding: 0;
  line-height: 1;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  @media (max-width: 768px) {
    width: 45px;
    height: 45px;
    font-size: 32px;
    left: 10px;
  }
`;

const NextButton = styled.button`
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid white;
  color: white;
  font-size: 48px;
  cursor: pointer;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  padding: 0;
  line-height: 1;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  @media (max-width: 768px) {
    width: 45px;
    height: 45px;
    font-size: 32px;
    right: 10px;
  }
`;

const ThumbnailStrip = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 10px;
  padding: 10px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 8px;
  overflow-x: auto;
  max-width: 90vw;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 3px;
  }
`;

const Thumbnail = styled.img`
  width: 50px;
  height: 50px;
  object-fit: cover;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.2s;
  flex-shrink: 0;

  &.active {
    border-color: #00ad23;
  }

  &:hover {
    border-color: white;
  }
`;

const Counter = styled.div`
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  color: white;
  font-family: Lato, sans-serif;
  font-size: 18px;
  background: rgba(0, 0, 0, 0.5);
  padding: 8px 16px;
  border-radius: 4px;
`;
